import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import backendApi from "../api/backendApi";
import {
  formatCurrency,
  formatPaidAt,
  formatPaymentMonth,
} from "../components/dashboard/dashboardUtils";

const STATUS_OPTIONS = ["INITIATED", "SUCCESS", "FAILED", "CANCELLED", "EXPIRED"];

const emptyFilters = () => ({
  paymentMonth: "",
  paidFrom: "",
  paidTo: "",
  status: "",
});

const DEFAULT_PAGE_SIZE = 20;
const USER_TRANSACTIONS_RETURN_KEY = "userTransactionsReturnTo";

const inputClass =
  "w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";

function getErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || error.message || "Failed to load transactions";
}

function compactFilters(filters) {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => String(value || "").trim())
  );
}

function filtersFromSearch(searchParams) {
  return {
    paymentMonth: searchParams.get("paymentMonth") || "",
    paidFrom: searchParams.get("paidFrom") || "",
    paidTo: searchParams.get("paidTo") || "",
    status: searchParams.get("status") || "",
  };
}

function positiveIntFromSearch(searchParams, key, fallback) {
  const value = Number(searchParams.get(key));
  return Number.isInteger(value) && value > 0 ? Math.min(value, 100) : fallback;
}

function pageFromSearch(searchParams) {
  const value = Number(searchParams.get("page"));
  return Number.isInteger(value) && value >= 0 ? value : 0;
}

function buildSearchParams(filters, page, size) {
  const next = new URLSearchParams();
  Object.entries(compactFilters(filters)).forEach(([key, value]) => {
    next.set(key, value);
  });
  if (page > 0) next.set("page", String(page));
  if (size !== DEFAULT_PAGE_SIZE) next.set("size", String(size));
  return next;
}

function StatusPill({ value }) {
  const status = String(value || "").toUpperCase();
  const className =
    {
      SUCCESS: "bg-emerald-100 text-emerald-700",
      FAILED: "bg-red-100 text-red-700",
      CANCELLED: "bg-orange-100 text-orange-700",
      EXPIRED: "bg-zinc-200 text-zinc-700",
      INITIATED: "bg-amber-100 text-amber-700",
    }[status] || "bg-zinc-100 text-zinc-700";

  return (
    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${className}`}>
      {status || "-"}
    </span>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm font-medium text-zinc-700">
      <span>{label}</span>
      {children}
    </label>
  );
}

function ReceiptAction({ transaction, returnTo }) {
  if (String(transaction.status || "").toUpperCase() !== "SUCCESS") {
    return (
      <span className="rounded-xl border border-zinc-200 px-3 py-2 text-xs font-semibold text-zinc-400">
        Receipt unavailable
      </span>
    );
  }

  return (
    <Link
      to={`/receipt/${transaction.id}`}
      state={{ returnTo }}
      className="rounded-xl bg-emerald-700 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-800"
    >
      View Receipt
    </Link>
  );
}

function TransactionCard({ transaction, returnTo }) {
  return (
    <article className="rounded-2xl bg-white p-4 shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-zinc-950">
            {transaction.paymentReference || "-"}
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            {formatPaymentMonth(transaction.paymentMonth)}
          </p>
        </div>
        <StatusPill value={transaction.status} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400">
            Paid At
          </p>
          <p className="mt-1 font-medium text-zinc-800">
            {formatPaidAt(transaction.paymentDate)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400">
            Amount
          </p>
          <p className="mt-1 font-semibold text-emerald-700">
            {formatCurrency(transaction.amount)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400">
            Method
          </p>
          <p className="mt-1 font-medium text-zinc-800">
            {transaction.method || "-"}
          </p>
        </div>
        <div className="flex items-end justify-end">
          <ReceiptAction transaction={transaction} returnTo={returnTo} />
        </div>
      </div>
    </article>
  );
}

export default function UserTransactionPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(() => pageFromSearch(searchParams));
  const [size, setSize] = useState(() =>
    positiveIntFromSearch(searchParams, "size", DEFAULT_PAGE_SIZE)
  );
  const [total, setTotal] = useState(0);
  const [draftFilters, setDraftFilters] = useState(() =>
    filtersFromSearch(searchParams)
  );
  const [filters, setFilters] = useState(() => filtersFromSearch(searchParams));

  const totalPages = useMemo(() => Math.ceil(total / size) || 1, [total, size]);
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const updateDraftFilter = (name, value) => {
    setDraftFilters((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(0);
    setFilters({ ...draftFilters });
  };

  const resetFilters = () => {
    setDraftFilters(emptyFilters());
    setFilters(emptyFilters());
    setPage(0);
  };

  useEffect(() => {
    const next = buildSearchParams(filters, page, size);
    const search = next.toString();
    const returnTo = `/transactions${search ? `?${search}` : ""}`;

    setSearchParams(next, { replace: true });
    sessionStorage.setItem(USER_TRANSACTIONS_RETURN_KEY, returnTo);
  }, [filters, page, size, setSearchParams]);

  useEffect(() => {
    let ignore = false;

    async function loadTransactions() {
      setLoading(true);
      setError(null);

      try {
        const res = await backendApi.get("/payment/me/transactions", {
          params: {
            page,
            size,
            ...compactFilters(filters),
          },
        });
        const data = res?.data;
        const content = Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data)
          ? data
          : [];

        if (!ignore) {
          setRows(content);
          setTotal(
            typeof data?.totalElements === "number"
              ? data.totalElements
              : content.length
          );
        }
      } catch (e) {
        if (!ignore) {
          setRows([]);
          setTotal(0);
          setError(getErrorMessage(e));
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    loadTransactions();

    return () => {
      ignore = true;
    };
  }, [page, size, filters]);

  const returnTo = `${location.pathname}${location.search}`;

  return (
    <div className="min-h-full bg-[#f6f3ea] p-4 md:rounded-3xl md:p-6">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Personal Account
            </p>
            <h1 className="mt-1 text-3xl font-black text-zinc-950">
              Transactions
            </h1>
            <p className="mt-1 text-sm text-zinc-500">
              Filter your own payments by reporting month, paid date, and status.
            </p>
          </div>
          <div className="text-sm text-zinc-500">
            Showing {rows.length} of {total} transaction{total === 1 ? "" : "s"}
          </div>
        </div>

        <form
          onSubmit={applyFilters}
          className="rounded-2xl bg-white p-4 shadow sm:p-5"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <FilterField label="Payment Month">
              <input
                type="month"
                value={draftFilters.paymentMonth}
                onChange={(e) =>
                  updateDraftFilter("paymentMonth", e.target.value)
                }
                className={inputClass}
              />
            </FilterField>

            <FilterField label="Paid From">
              <input
                type="date"
                value={draftFilters.paidFrom}
                onChange={(e) => updateDraftFilter("paidFrom", e.target.value)}
                className={inputClass}
              />
            </FilterField>

            <FilterField label="Paid To">
              <input
                type="date"
                value={draftFilters.paidTo}
                onChange={(e) => updateDraftFilter("paidTo", e.target.value)}
                className={inputClass}
              />
            </FilterField>

            <FilterField label="Status">
              <select
                value={draftFilters.status}
                onChange={(e) => updateDraftFilter("status", e.target.value)}
                className={inputClass}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </FilterField>

            <FilterField label="Page Size">
              <select
                value={size}
                onChange={(e) => {
                  setPage(0);
                  setSize(Number(e.target.value));
                }}
                className={inputClass}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}/page
                  </option>
                ))}
              </select>
            </FilterField>
          </div>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-zinc-500">
              {activeFilterCount > 0
                ? `${activeFilterCount} filter${
                    activeFilterCount === 1 ? "" : "s"
                  } applied`
                : "No filters applied"}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={resetFilters}
                className="rounded-xl border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50"
              >
                Reset
              </button>
              <button
                type="submit"
                className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loading}
              >
                Search
              </button>
            </div>
          </div>
        </form>

        <div className="hidden rounded-2xl bg-white shadow md:block">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Transaction ID</th>
                  <th className="px-4 py-3 font-medium">Payment Month</th>
                  <th className="px-4 py-3 font-medium">Paid At</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Method</th>
                  <th className="px-4 py-3 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td className="px-4 py-6" colSpan={7}>
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && error && (
                  <tr>
                    <td className="px-4 py-6 text-red-600" colSpan={7}>
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td className="px-4 py-6" colSpan={7}>
                      No transactions found.
                    </td>
                  </tr>
                )}
                {!loading &&
                  !error &&
                  rows.map((transaction) => (
                    <tr key={transaction.id} className="border-t hover:bg-zinc-50">
                      <td className="px-4 py-3">
                        {transaction.paymentReference || "-"}
                      </td>
                      <td className="px-4 py-3">
                        {formatPaymentMonth(transaction.paymentMonth)}
                      </td>
                      <td className="px-4 py-3">
                        {formatPaidAt(transaction.paymentDate)}
                      </td>
                      <td className="px-4 py-3 font-semibold text-emerald-700">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill value={transaction.status} />
                      </td>
                      <td className="px-4 py-3">{transaction.method || "-"}</td>
                      <td className="px-4 py-3 text-right">
                        <ReceiptAction transaction={transaction} returnTo={returnTo} />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-3 md:hidden">
          {loading && (
            <div className="rounded-2xl bg-white p-4 text-sm shadow">
              Loading...
            </div>
          )}
          {!loading && error && (
            <div className="rounded-2xl bg-white p-4 text-sm text-red-600 shadow">
              {error}
            </div>
          )}
          {!loading && !error && rows.length === 0 && (
            <div className="rounded-2xl bg-white p-4 text-sm shadow">
              No transactions found.
            </div>
          )}
          {!loading &&
            !error &&
            rows.map((transaction) => (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                returnTo={returnTo}
              />
            ))}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-zinc-500">
            Page {page + 1} / {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              className="rounded-lg border bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={page === 0 || loading}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Prev
            </button>
            <button
              className="rounded-lg border bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={page + 1 >= totalPages || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
